<template>
  <nav>
    <header>
      <litera-logo class="logo"/>
      <base-input
          v-model="search"
          type="text"
          placeholder="Search"/>
      <base-button
          title="New Note"
          icon="post_add"
          type="transparent"
          @click="newNote"/>
    </header>
    <main>
      <div class="tags">
        <ul class="static-tags">
          <tag-entry
              icon="label"
              text="All notes"/>
          <tag-entry
              icon="label_off"
              text="Untagged"
              tag="untagged"/>
          <tag-entry
              icon="archive"
              text="Archive"
              tag="archived"/>
          <tag-entry
              icon="delete"
              text="Bin"
              tag="deleted"/>
        </ul>
        <ul
            v-show="pinnedTags.length !== 0"
            class="pinned-tags">
          <custom-tag-entry
              v-for="tag of pinnedTags"
              :key="tag.name"
              :tag="tag"/>
        </ul>
        <ul class="unpinned-tags">
          <custom-tag-entry
              v-for="tag of unpinnedTags"
              :key="tag.name"
              :tag="tag"/>
        </ul>
      </div>
      <div class="notes">
        <div
            v-if="notes.length !== 0 && deleted"
            class="notes-delete">
          <base-button
              icon="restore"
              text="Restore All"
              @click="restoreAll"/>
          <base-button
              icon="delete"
              text="Empty Bin"
              @click="emptyBin"/>
        </div>
        <div
            v-if="notes.length === 0"
            class="notes-empty">
          <div class="icon">note</div>
          <span>No notes!</span>
        </div>
        <ul v-else>
          <sidebar-entry
              v-for="note in notes"
              :key="note.uuid"
              :note="note"/>
        </ul>
      </div>
    </main>
    <footer>
      <span>
        {{ notes.length }} notes
      </span>
      <base-button
          icon="settings"
          type="transparent"
          @click="goToSettings"/>
    </footer>
  </nav>
</template>

<script>
import { mapState } from 'vuex';

import BaseButton from '@/components/core/BaseButton.vue';
import BaseInput from '@/components/core/BaseInput.vue';
import CustomTagEntry from '@/components/sidebar/CustomTagEntry.vue';
import MessageModal from '@/components/core/modal/MessageModal.vue';
import SidebarEntry from '@/components/sidebar/SidebarEntry.vue';
import TagEditModal from '@/components/sidebar/TagEditModal.vue';
import TagEntry from '@/components/sidebar/TagEntry.vue';

import LiteraLogo from '@/assets/logo.svg';

export default {
  name: 'TheSidebar',
  components: {
    BaseButton,
    BaseInput,
    CustomTagEntry,
    LiteraLogo,
    SidebarEntry,
    TagEntry,
  },
  computed: {
    uuid() {
      return this.$route.params.id;
    },
    deleted() {
      return this.$route.query.deleted && JSON.parse(this.$route.query.deleted);
    },
    ...mapState({
      notes: state => state.notes.notes,
      storageProviderReady: state => state.storageProviderReady,
      tags: state => state.tags.tags,
    }),
    pinnedTags() {
      return this.tags.filter(tag => tag.pinned);
    },
    unpinnedTags() {
      return this.tags.filter(tag => !tag.pinned);
    },
    search: {
      get() {
        return this.$route.query.search;
      },
      set(value) {
        // Delay by 100ms?
        const query = {
          ...this.$route.query,
        };

        if (!value) {
          delete query.search;
        } else {
          query.search = value;
        }

        this.$router.replace({
          query,
        });
      },
    },
  },
  created() {
    this.loadData();
  },
  methods: {
    async loadData() {
      // Don't load data if provider isn't ready
      // Callbacks and watchers will call this method again once it's set
      if (!this.storageProviderReady) {
        return;
      }

      const {
        tag, search, untagged, archived,
      } = this.$route.query;
      const params = {
        limit: 0,
        tag,
        search,
      };

      if (untagged) {
        params.untagged = JSON.parse(untagged);
      }
      if (archived) {
        params.archived = JSON.parse(archived);
      }
      if (this.deleted) {
        params.deleted = this.deleted;
      }

      this.$store.commit('notes/setParams', params);
      this.$store.dispatch('notes/getNotes');
    },
    tagLink(tag) {
      return {
        query: {
          ...this.$route.query,
          tag,
        },
      };
    },
    editTag(tagName) {
      this.$modal.show({
        component: TagEditModal,
        properties: {
          tagName,
        },
      });
    },
    async pinTag(tag) {
      tag.pinned = !tag.pinned;
      await this.$store.dispatch(
        'tags/updateTag',
        { tag },
      );
    },
    async newNote() {
      const uuid = await this.$store.dispatch('notes/newNote');
      this.$router.push({
        name: 'note-editor',
        params: {
          id: uuid,
        },
      });
    },
    restoreAll() {
      this.$store.dispatch('notes/restoreDeleted');
    },
    emptyBin() {
      this.$modal.show({
        component: MessageModal,
        properties: {
          icon: 'delete',
          title: 'Empty Bin',
          message: 'Are you sure you want to empty the bin?',
          buttons: [
            {
              text: 'Cancel',
              func: this.$modal.dismiss,
            },
            {
              text: 'Delete',
              func: () => {
                this.$store.dispatch('notes/purgeDeleted');
                this.$modal.dismiss();
                this.$router.push({
                  name: 'notes',
                  query: {
                    deleted: true,
                  },
                }).catch(() => {});
              },
            },
          ],
        },
      });
    },
    goToSettings() {
      this.$store.commit('setRouteQuery', this.$route.query);
      this.$router.push({
        name: 'settings-general',
      }).catch(() => {});
    },
  },
  watch: {
    storageProviderReady() {
      this.loadData();
    },
    $route(to, from) {
      if (to.query.tag === from.query.tag
          && to.query.search === from.query.search
          && to.query.untagged === from.query.untagged
          && to.query.archived === from.query.archived
          && to.query.deleted === from.query.deleted) {
        return;
      }
      this.loadData();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/colors.scss';

$header-height: 4rem;
$footer-height: 3rem;

$tag-container-width: 3rem;
$tag-container-expanded-width: 16rem;
$tag-container-transition-duration: .3s;

nav {
  bottom: 0;
  left: 0rem;
  position: absolute;
  right: 0;
  top: 0;
  display: flex;
  flex-direction: column;

  header {
    align-items: center;
    background: $dark-gray-700;
    border-bottom: 0.0625rem solid $dark-gray-900;
    display: flex;
    height: $header-height;
    padding: 0.25rem;

    .logo {
      height: 2rem;
      margin: 0.5rem;
      width: 2rem;
    }

    input {
      flex-grow: 1;
      margin: 0.375rem 0 0.375rem 0.375rem;
    }
  }

  main {
    flex-grow: 1;
    position: relative;

    .tags {
      background: $dark-gray-700;
      border-right: 0.0625rem solid $dark-gray-900;
      bottom: 0;
      display: flex;
      flex-direction: column;
      left: 0;
      overflow: hidden;
      position: absolute;
      top: 0;
      transition: width $tag-container-transition-duration;
      width: $tag-container-width;
      z-index: 1;

      &:hover {
        width: $tag-container-expanded-width;

        ul.unpinned-tags {
          overflow-y: auto;

          li:not(.active) {
            height: 2.5rem;
            opacity: 1;
            pointer-events: all;
          }
        }
      }

      ul {
        width: $tag-container-expanded-width;
        list-style: none;
        display: flex;
        flex-direction: column;

        &.static-tags {
          max-height: 30vh;
          flex-shrink: 0;
        }

        &.pinned-tags {
          flex-shrink: 0;
          margin-top: 1.5rem;
        }

        &.unpinned-tags {
          flex-grow: 1;
          margin-top: 1.5rem;
          overflow-y: hidden;

          li:not(.active) {
            height: 0;
            opacity: 0;
            transition: opacity $tag-container-transition-duration,
                        height $tag-container-transition-duration;
            pointer-events: none;
          }
        }
      }
    }

    .notes {
      background: $dark-gray-700;
      bottom: 0;
      display: flex;
      flex-direction: column;
      left: $tag-container-width;
      overflow: hidden;
      position: absolute;
      right: 0;
      top: 0;

      &-delete {
        border-bottom: 0.0625rem solid $dark-gray-900;
        display: flex;

        button {
          flex-grow: 1;
        }
      }

      &-empty {
        align-items: center;
        display: flex;
        flex-direction: column;
        flex-grow: 1;
        justify-content: center;

        .icon {
          color: $secondary-text;
          font-size: 8rem;
          height: 8rem;
          line-height: 8rem;
          width: 8rem;
        }

        span {
          font-size: 1.25rem;
          margin-top: 1rem;
        }
      }

      ul {
        flex-grow: 1;
        list-style: none;
        overflow: auto;
      }
    }
  }

  footer {
    align-items: center;
    background: $dark-gray-700;
    border-top: 0.0625rem solid $dark-gray-900;
    display: flex;
    justify-content: space-between;

    span {
      margin: 0 0 0 0.75rem;
    }
  }
}

</style>
